import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { authService } from '../services/AuthService';
import { userService } from '../services/UserService';
import { User } from '../models/User';
import { ensureUserDocument } from '../utils/ensureUserDocument';

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User | null>;
  signUp: (
    email: string,
    password: string,
    displayName: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        // Ensure user document exists with all required fields (works for all users)
        await ensureUserDocument(fbUser);

        // Fetch user data from Firestore
        const userData = await userService.getUser(fbUser.uid);

        // Debug: surface the role resolved from the Firestore user document
        console.log(
          `[Auth] onAuthStateChanged — ${fbUser.email} (uid ${fbUser.uid}) → role: ${userData?.role ?? 'NONE'}`
        );

        setUser(userData);

        // Update last login
        if (userData) {
          await userService.updateLastLogin(fbUser.uid);
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    // Trigger Firebase sign in
    const fbUser = await authService.signIn(email, password);

    // Ensure user document exists
    await ensureUserDocument(fbUser);

    // Fetch user data directly instead of waiting for onAuthStateChanged
    const userData = await userService.getUser(fbUser.uid);

    // Debug: surface the role resolved from the Firestore user document
    console.log(
      `[Auth] signIn — ${fbUser.email} (uid ${fbUser.uid}) → role: ${userData?.role ?? 'NONE'}`
    );

    // Update state
    setFirebaseUser(fbUser);
    setUser(userData);

    // Update last login
    if (userData) {
      await userService.updateLastLogin(fbUser.uid);
    }

    return userData;
  };

  const signUp = async (
    email: string,
    password: string,
    displayName: string
  ) => {
    await authService.signUp(email, password, displayName);
  };

  const signOut = async () => {
    await authService.signOut();
  };

  const resetPassword = async (email: string) => {
    await authService.resetPassword(email);
  };

  const refreshUser = async () => {
    if (firebaseUser) {
      const userData = await userService.getUser(firebaseUser.uid);
      setUser(userData);
    }
  };

  const value: AuthContextType = {
    firebaseUser,
    user,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
